// Admin → Users. Searchable, filterable list of every account. Click a row to
// drill into the detail (suspend / reactivate / reset password / impersonate).
// docs/replan-v2.md (admin console). English chrome (staff tool).
// Migrated to the console component system in M7c / P6.

import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import {
  DataTable,
  FilterBar,
  PageHeader,
  StatusBadge,
  actionsColumn,
  useDebounced,
  useTableState,
  type Column,
} from '~/components';
import { UserEditDialog } from '~/components/family/UserEditDialog';
import { getUser, listUsers, type AdminUserRow } from '~/lib/admin';

export const Route = createFileRoute('/_authed/users/')({
  component: UsersPage,
});

function UsersPage() {
  const navigate = useNavigate();
  const table = useTableState({ filterKeys: ['status', 'kind'] });
  // The row carries only the list projection; the edit dialog needs the full
  // record (country, phone, verification stamp) so it fetches on open rather
  // than editing a partial and blanking the fields the list never returned.
  const [editingId, setEditingId] = useState<string | null>(null);
  // The list is filtered SERVER-side, so an un-debounced search would fire a
  // request per keystroke. The previous version did exactly that.
  const q = useDebounced(table.q.trim(), 300);

  const status = table.filters.status ?? 'all';
  const kind = table.filters.kind ?? 'all';

  const users = useQuery({
    queryKey: ['users', q, status, kind],
    queryFn: ({ signal }) =>
      listUsers(
        {
          q: q || undefined,
          status: status as 'all' | 'active' | 'suspended' | 'invited',
          kind: kind as 'all' | 'adult' | 'learner',
        },
        signal,
      ),
  });

  // Keyed by user id and sharing the ['user', id] key with the detail route, so
  // opening the dialog for someone already visited is instant and an edit made
  // here invalidates the detail page's copy too.
  const editingUser = useQuery({
    queryKey: ['user', editingId],
    queryFn: ({ signal }) => getUser(editingId!, signal),
    enabled: editingId != null,
  });

  const columns: Column<AdminUserRow>[] = [
    {
      key: 'user',
      header: 'User',
      alwaysVisible: true,
      render: (user) => (
        <div className="min-w-0">
          <Link
            to="/users/$id"
            params={{ id: user.id }}
            onClick={(e) => e.stopPropagation()}
            className="truncate font-medium text-admin-900 hover:underline"
          >
            {user.displayName ?? user.email ?? user.username ?? user.id.slice(0, 8)}
          </Link>
          <p className="mt-0.5 truncate text-xs text-admin-500">
            {user.email ?? `@${user.username ?? '—'}`}
          </p>
        </div>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      hideBelow: 'md',
      render: (user) =>
        user.roles.length === 0 ? (
          <span className="text-admin-300">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {user.roles.map((r) => (
              <StatusBadge
                key={r}
                status={r}
                label={r}
                // Staff roles read as elevated privilege, so they carry the
                // brand tone rather than sitting neutral among ordinary roles.
                tone={r.includes('admin') ? 'info' : 'neutral'}
              />
            ))}
          </div>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user) => <StatusBadge status={user.status} dot />,
    },
    {
      key: 'createdAt',
      header: 'Joined',
      align: 'end',
      hideBelow: 'md',
      render: (user) => (
        <span className="text-xs text-admin-500">{formatDate(user.createdAt)}</span>
      ),
    },
    // Editing identity + profile is available INLINE (the dialog only needs the
    // user id, which the row has). The genuinely contextual actions — suspend,
    // reset password, impersonate — still live on the detail page, because they
    // need the roles and membership the row does not carry.
    actionsColumn<AdminUserRow>({
      label: (user) => user.displayName ?? user.email ?? user.username ?? user.id.slice(0, 8),
      primary: (user) => ({
        id: 'edit',
        label: 'Edit',
        icon: 'edit',
        onSelect: () => setEditingId(user.id),
      }),
      actions: (user) => [
        {
          id: 'open',
          label: 'Open account',
          icon: 'settings',
          onSelect: () => void navigate({ to: '/users/$id', params: { id: user.id } }),
        },
        {
          id: 'copy-email',
          label: 'Copy email',
          icon: 'copy',
          onSelect: () => void navigator.clipboard?.writeText(user.email ?? ''),
          disabled: !user.email,
          disabledReason: 'Learners sign in with a username, not an email.',
        },
      ],
    }),
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Users" description="Every account — parents, learners, teachers, staff." />

      <DataTable
        columns={columns}
        rows={users.data ?? []}
        rowKey={(u) => u.id}
        loading={users.isLoading}
        error={users.error}
        onRetry={() => void users.refetch()}
        onRowClick={(u) => void navigate({ to: '/users/$id', params: { id: u.id } })}
        columnVisibility
        caption="Users"
        empty={{
          icon: 'users',
          title: table.isFiltered ? 'No users match these filters' : 'No users yet',
          description: table.isFiltered
            ? 'Try clearing the filters or searching for something else.'
            : 'Accounts appear here as people sign up.',
        }}
        toolbar={
          <FilterBar
            query={table.q}
            onQueryChange={table.setQuery}
            searchPlaceholder="Search email, username, name…"
            filters={[
              {
                key: 'status',
                label: 'Status',
                value: status === 'all' ? '' : status,
                options: [
                  { value: 'active', label: 'Active' },
                  { value: 'suspended', label: 'Suspended' },
                  { value: 'invited', label: 'Invited' },
                ],
              },
              {
                key: 'kind',
                label: 'Kind',
                value: kind === 'all' ? '' : kind,
                options: [
                  { value: 'adult', label: 'Adults' },
                  { value: 'learner', label: 'Learners' },
                ],
              },
            ]}
            onFilterChange={table.setFilter}
            onClear={table.clearFilters}
            isFiltered={table.isFiltered}
          />
        }
      />

      <UserEditDialog
        open={editingId != null}
        onOpenChange={(open) => !open && setEditingId(null)}
        user={editingUser.data ?? null}
        invalidateKeys={[['users']]}
      />
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
